import dayjs from "dayjs";

/** Opens the WhatsApp reconnect modal when the integrations page loads */
export const WHATSAPP_META_RECONNECT_PARAM = "reconnect";

export const FACEBOOK_META_CALLBACK_PATH =
  "/integration/facebook-meta/callback";

export const getFacebookMetaRedirectUri = () =>
  `${window.location.origin}${FACEBOOK_META_CALLBACK_PATH}`;

/** Opened in a popup rather than through the Facebook SDK: the SDK on this page
 * is initialised with the WhatsApp app, and one page cannot hold two
 */
export const getFacebookMetaLoginUrl = (state: string) => {
  const params = new URLSearchParams({
    client_id: process.env.REACT_APP_FACEBOOK_META_APP_ID,
    config_id: process.env.REACT_APP_FACEBOOK_META_CONFIG_ID,
    response_type: "code",
    redirect_uri: getFacebookMetaRedirectUri(),
    state,
  });

  return `https://www.facebook.com/v26.0/dialog/oauth?${params.toString()}`;
};

export const formatChatListTimestamp = (value: Date | string) => {
  const date = dayjs(value);

  if (!date.isValid()) {
    return "";
  }

  const now = dayjs();

  if (date.isSame(now, "day")) {
    return date.format("HH:mm");
  }

  if (date.isAfter(now.subtract(6, "day"))) {
    return date.format("ddd");
  }

  return date.isSame(now, "year")
    ? date.format("DD/MM")
    : date.format("DD/MM/YY");
};

import { Conversation } from "../../../model/integration/dto/meta/MetaConversation";
import { Integration } from "../../../model/integration/Integration";
import { IntegrationConfigWhatsappMeta } from "../../../model/integration/types/config/IntegrationConfigWhatsappMeta";
import { WAConversationHandler } from "../../../model/integration/types/whatsapp/WAConversationHandler.enum";

export const isChatHandledByAgent = (
  chat: Conversation | undefined,
  integration: Integration<IntegrationConfigWhatsappMeta> | null | undefined,
) =>
  Boolean(
    integration?.config?.aiAgent?.enabled &&
      chat?.handledBy === WAConversationHandler.AI,
  );

/** A chat the AI agent gave up on that nobody has answered yet.
 *
 * Deliberately not `handledBy === HUMAN` on its own: that is also true of every
 * chat a person took over deliberately, and of every chat on an integration with
 * no agent at all. What makes this a queue item is that the agent dropped it
 * (no `byUserId`) and no one has replied since (no `resolvedAt`).
 */
export const chatNeedsAttention = (chat: Conversation | undefined) =>
  Boolean(
    chat?.handledBy === WAConversationHandler.HUMAN &&
      chat?.handover?.at &&
      !chat.handover.byUserId &&
      !chat.handover.resolvedAt,
  );

/** Where a chat belongs in the list. One nobody has written in yet has no
 * last message, and read as 0 it sank below every chat that has one - so a chat
 * just started from the inbox landed at the very bottom. A chat older than the
 * timestamps has neither, and goes by the time in its id. An imported chat
 * nobody has written in goes to the very end. The server ranks the same way.
 */
export const chatRecency = (
  chat: Pick<
    Conversation,
    "_id" | "lastMessageAt" | "createdAt" | "importedAt"
  >,
) => {
  if (!chat.lastMessageAt && chat.importedAt) {
    return 0;
  }

  return (
    new Date(chat.lastMessageAt || chat.createdAt || "").getTime() ||
    parseInt(String(chat._id).slice(0, 8), 16) * 1000 ||
    0
  );
};

/** Newest first, and ties broken by id the way the server breaks them, so the
 * list reads in exactly the order its pages arrive in
 */
export const byRecency = (
  a: Pick<Conversation, "_id" | "lastMessageAt" | "createdAt" | "importedAt">,
  b: Pick<Conversation, "_id" | "lastMessageAt" | "createdAt" | "importedAt">,
) =>
  chatRecency(b) - chatRecency(a) ||
  (a._id < b._id ? 1 : a._id > b._id ? -1 : 0);

export const loadFacebookSdk = async () => {
  const appId = process.env.REACT_APP_WHATSAPP_META_APP_ID;

  if (!appId) {
    throw new Error("Missing REACT_APP_WHATSAPP_META_APP_ID");
  }

  if (window.FB) {
    return window.FB;
  }

  await new Promise<void>((resolve, reject) => {
    const existingScript = document.getElementById(
      "facebook-jssdk",
    ) as HTMLScriptElement | null;

    window.fbAsyncInit = () => {
      window.FB?.init({
        appId,
        autoLogAppEvents: true,
        xfbml: false,
        version: "v25.0",
      });
      resolve();
    };

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(), {
        once: true,
      });
      return;
    }

    const script = document.createElement("script");
    script.id = "facebook-jssdk";
    script.async = true;
    script.defer = true;
    script.src = "https://connect.facebook.net/en_US/sdk.js";
    script.onerror = () => reject(new Error("Failed to load Facebook SDK"));
    document.body.appendChild(script);
  });

  if (!window.FB) {
    throw new Error("Facebook SDK did not initialize");
  }

  return window.FB;
};

export const whatsappChatScrollToBottom = () => {
  const el = document.body.querySelector(".atira-meta-conversation-panel");

  if (el) {
    el.scrollTop = el.scrollHeight;
  }
};

const loadGapiPicker = async () => {
  if (window.gapi?.load && window.google?.picker) {
    return;
  }

  await new Promise<void>((resolve, reject) => {
    const existingScript = document.getElementById(
      "google-picker-api",
    ) as HTMLScriptElement | null;

    const onLoaded = () => {
      window.gapi?.load("picker", () => resolve());
    };

    if (existingScript) {
      if (window.gapi?.load) {
        onLoaded();
      } else {
        existingScript.addEventListener("load", onLoaded, { once: true });
      }
      return;
    }

    const script = document.createElement("script");
    script.id = "google-picker-api";
    script.async = true;
    script.defer = true;
    script.src = "https://apis.google.com/js/api.js";
    script.onload = onLoaded;
    script.onerror = () => reject(new Error("Failed to load Google Picker"));
    document.body.appendChild(script);
  });
};

/** Opens Google's Picker so the org owner chooses which files/folders Atira
 * Space is allowed to show/manage, under the short-lived access token the
 * OAuth callback just handed back. The OAuth scope itself can already reach
 * all of "My Drive" - this is where the team draws their own boundary on
 * what the app actually displays.
 */
export const openGoogleDrivePicker = async (
  accessToken: string,
): Promise<{ fileId: string; name: string; mimeType: string }[]> => {
  await loadGapiPicker();

  const apiKey = process.env.REACT_APP_GOOGLE_PICKER_API_KEY;
  const clientId = process.env.REACT_APP_GOOGLE_DRIVE_CLIENT_ID;

  if (!window.google?.picker || !apiKey || !clientId) {
    throw new Error("Google Picker is not configured");
  }

  const { picker } = window.google;

  const pickerResult = new Promise<
    { fileId: string; name: string; mimeType: string }[]
  >((resolve) => {
    const view = new picker.DocsView(picker.ViewId.DOCS)
      .setIncludeFolders(true)
      .setSelectFolderEnabled(true);

    const instance = new picker.PickerBuilder()
      .addView(view)
      .setOAuthToken(accessToken)
      .setDeveloperKey(apiKey)
      .setAppId(clientId)
      .enableFeature(picker.Feature.MULTISELECT_ENABLED)
      .setCallback((data: any) => {
        if (data.action === picker.Action.PICKED) {
          resolve(
            (data.docs || []).map((doc: any) => ({
              fileId: doc.id,
              name: doc.name,
              mimeType: doc.mimeType,
            })),
          );
        } else if (
          data.action === picker.Action.CANCEL ||
          data.action === picker.Action.CANCEL_HIDE
        ) {
          resolve([]);
        }
      })
      .build();

    instance.setVisible(true);
  });

  return Promise.race([
    pickerResult,
    new Promise<never>((_, reject) =>
      setTimeout(
        () => reject(new Error("Google Picker did not respond in time")),
        20000,
      ),
    ),
  ]);
};
